import React, { useState } from 'react';
import {
  AppointmentProduct,
  AppointmentType,
  Appointment,
  TimeSlot,
  Clinic,
  ValidPromo,
  PatientMembership
} from 'kb-shared';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import Honeybadger from '@honeybadger-io/js';
import ConfirmAppointmentDetails from './ConfirmationAppointmentDetails';
import usePromo from '../../../hooks/usePromo';
import { ConfirmDetailsWrapper } from '../steps/styles';

const PATIENT_MEMBERSHIP = gql`
  query patientMembership {
    patient {
      id
      patientMembership {
        id
        employer
        membershipRules {
          code
          id
          discountId
        }
      }
    }
  }
`;

interface Props {
  clinic: Clinic | null;
  appointment: AppointmentType | Appointment | null;
  product: AppointmentProduct<AppointmentType>;
  timeSlot: TimeSlot | null;
  isReschedule?: boolean;
  loading?: boolean;
  paymentTermsChecked: boolean;
  onPaymentTermsChecked: (checked: boolean) => void;
  onValidPromoChange: (validPromo: ValidPromo | null) => void;
  validPromo: ValidPromo | null;
}

const AppointmentSummaryDetails = (props: Props) => {
  const {
    appointment,
    timeSlot,
    clinic,
    paymentTermsChecked,
    onPaymentTermsChecked,
    onValidPromoChange,
    isReschedule = false,
    loading = false,
    product,
    validPromo
  } = props;
  const [membershipPromoCode, setMembershipPromoCode] = useState<string | null>(null);
  const { validatePromo } = usePromo(product);

  return (
    <Query
      query={PATIENT_MEMBERSHIP}
      onCompleted={(data: PatientMembership) => {
        // If the user has an existing membership, we want to
        // attempt to apply the promo/discount code associated
        // with their membership.
        if (!membershipPromoCode) {
          const membership = data.patient.patientMembership;
          if (membership && membership.membershipRules[0]) {
            // We take the first (as of now only) "membershipRules"
            // which contains a promo/discount code
            const firstRule = membership.membershipRules[0];
            const discountCode = firstRule.code;
            setMembershipPromoCode(discountCode);
            validatePromo(discountCode)
              .then(({ validPromo }) => {
                onValidPromoChange(validPromo || null);
              })
              .catch(error => Honeybadger.notify(error));
          }
        }
      }}
    >
      {() => {
        return (
          <ConfirmDetailsWrapper>
            <ConfirmAppointmentDetails
              loading={loading}
              appointment={appointment}
              timeSlot={timeSlot}
              clinic={clinic}
              termsAccepted={paymentTermsChecked}
              isReschedule={isReschedule}
              discountPrice={validPromo && validPromo.totalCents}
              onTermsAcceptToggled={() => {
                onPaymentTermsChecked(!paymentTermsChecked);
              }}
            />
          </ConfirmDetailsWrapper>
        );
      }}
    </Query>
  );
};

export default AppointmentSummaryDetails;
